import { useEffect, useRef, useState } from 'react';

const BAR_WIDTH = 2;
const BAR_GAP = 3;
const MIN_BAR_HEIGHT = 2;
const MAX_BAR_HEIGHT = 22;
const MIN_BARS = 12;
const MAX_BARS = 140;

// Speech RMS sits well under 0.2, so scale it up before mapping to bar height.
const LEVEL_GAIN = 6;

// Deliberately low. Too high cuts people off mid-thought and loses their audio; too low
// just means the auto-stop never fires and the caller's duration cap catches it instead.
const SILENCE_LEVEL = 0.08;
const SILENCE_TIMEOUT_MS = 30_000;
const SILENCE_WARNING_MS = 22_000;

const formatElapsed = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

interface DictationIndicatorProps {
  stream: MediaStream | null;
  transcribing: boolean;
  /** Fired once when speech is followed by a sustained silence. */
  onSilenceTimeout: () => void;
}

/**
 * Inline dictation state for the composer. It takes the textarea's place while the
 * mic is live so the status sits on the control it describes, and it renders real
 * mic amplitude so the user can see they are actually being heard.
 *
 * The strip scrolls right-to-left and keeps roughly the last two seconds on screen,
 * so a passing sound leaves a visible trace instead of a single missable frame.
**/
function DictationIndicator({ stream, transcribing, onSilenceTimeout }: DictationIndicatorProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const levelsRef = useRef<number[]>([]);
  const [barCount, setBarCount] = useState<number>(MIN_BARS);
  // Read by the sampling loop, which must not tear down the audio graph on resize.
  const barCountRef = useRef<number>(MIN_BARS);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [silenceWarning, setSilenceWarning] = useState<boolean>(false);

  // Kept in a ref so a new callback identity never restarts the audio graph.
  const onSilenceTimeoutRef = useRef(onSilenceTimeout);
  useEffect(() => {
    onSilenceTimeoutRef.current = onSilenceTimeout;
  });

  /** 
   * Fit the waveform to whatever width the composer gives us
  **/
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const measure = () => {
      const width = strip.clientWidth;
      if (!width) return;

      const fitted = Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP));
      setBarCount(Math.min(Math.max(fitted, MIN_BARS), MAX_BARS));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [transcribing]);

  useEffect(() => {
    barCountRef.current = barCount;
  }, [barCount]);

  /** 
   * Drive the bar heights from live mic amplitude, writing to the DOM directly so
   * the 60fps sampling never re-renders React, and watch for a sustained silence
  **/
  useEffect(() => {
    if (!stream || transcribing) return;

    const AudioContextClass = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioContext = new AudioContextClass();

    // A context constructed after `getUserMedia` resolves can start out suspended,
    // which reads as pure silence.
    void audioContext.resume();

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    const samples = new Uint8Array(analyser.fftSize);
    let frame = 0;
    let lastSoundAt = performance.now();
    let heardSpeech = false;
    let warned = false;

    const render = () => {
      analyser.getByteTimeDomainData(samples);

      let sumSquares = 0;
      for (let i = 0; i < samples.length; i++) {
        const sample = (samples[i] - 128) / 128;
        sumSquares += sample * sample;
      }
      const level = Math.min(Math.sqrt(sumSquares / samples.length) * LEVEL_GAIN, 1);

      const count = barCountRef.current;
      const levels = levelsRef.current;
      levels.push(level);
      while (levels.length > count) levels.shift();

      // Newest sample lands on the last bar; anything before the history starts is silent.
      const offset = count - levels.length;
      for (let index = 0; index < count; index++) {
        const bar = barRefs.current[index];
        if (!bar) continue;

        const value = index < offset ? 0 : levels[index - offset];
        bar.style.height = `${MIN_BAR_HEIGHT + (value * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT))}px`;
      }

      const now = performance.now();
      if (level > SILENCE_LEVEL) {
        lastSoundAt = now;
        heardSpeech = true;
        if (warned) {
          warned = false;
          setSilenceWarning(false);
        }
      } else if (heardSpeech) {
        // Gated on having heard something first, so a dead or muted input can never
        // auto-stop a recording it simply cannot measure. The caller's duration cap
        // is the backstop for that case.
        const silentFor = now - lastSoundAt;

        if (silentFor >= SILENCE_TIMEOUT_MS) {
          onSilenceTimeoutRef.current();
          return;
        }

        if (silentFor >= SILENCE_WARNING_MS && !warned) {
          warned = true;
          setSilenceWarning(true);
        }
      }

      frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void audioContext.close();
    };
  }, [stream, transcribing]);

  /** 
   * Track recording duration
  **/
  useEffect(() => {
    if (transcribing) return;

    const interval = window.setInterval(() => setElapsedSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(interval);
  }, [transcribing]);

  if (transcribing) {
    return (
      <div role="status" className="flex h-8 w-full items-center gap-3 px-1">
        <span className="h-1.5 min-w-0 flex-1 animate-pulse rounded-full bg-black/10 dark:bg-white/15" aria-hidden="true" />
        <span className="shrink-0 text-[15px] text-black/45 dark:text-white/55">Transcribing…</span>
      </div>
    );
  }

  return (
    <div role="status" className="flex h-8 w-full items-center gap-3 px-1">
      <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500" aria-hidden="true" />

      <div
        ref={stripRef}
        className={`flex h-6 min-w-0 flex-1 items-center overflow-hidden transition-opacity ${silenceWarning ? 'opacity-40' : ''}`}
        style={{ gap: `${BAR_GAP}px` }}
        aria-hidden="true"
      >
        {Array.from({ length: barCount }, (_, index) => (
          <span
            key={index}
            ref={(node) => { barRefs.current[index] = node; }}
            className="shrink-0 rounded-full bg-red-500/85"
            style={{ width: `${BAR_WIDTH}px`, height: `${MIN_BAR_HEIGHT}px` }}
          />
        ))}
      </div>

      {silenceWarning ?
        <span className="shrink-0 text-[13px] text-black/45 dark:text-white/55">No sound — stopping soon</span>
      : null}

      <span className="shrink-0 text-[13px] tabular-nums text-black/45 dark:text-white/55">{formatElapsed(elapsedSeconds)}</span>

      <span className="sr-only">Listening. Press the mic button to finish, or Escape to cancel.</span>
    </div>
  );
}

export default DictationIndicator;
